/**
 * Field writers shared by the CLW review grid (reviewEdits.js) and the copy
 * workflow (CopyCampaignWorkflow/copyReviewEdits.js). Pure data — no React,
 * no I/O.
 *
 * These were born in copyReviewEdits.js (the copy grid shipped editable
 * flight/budget/pacing/status cells first) and are extracted here so the two
 * appliers can't drift: both grids render cells through the SAME row
 * builders, so an edit committed on either surface must write the SAME
 * underlying key for the cell to round-trip.
 *
 * Contract shared by every writer: enum-valued / structured fields only
 * accept input they can recognize — an unparseable draft is a NO-OP, and the
 * grid re-rendering the unchanged value is what tells the user the edit
 * didn't take. `numeric` below holds money/volume drafts to the SAME contract.
 */

// A money / volume draft the grid can commit WITHOUT guessing, or null.
//
// The old implementation was `Number(String(v).replace(/[^0-9.-]/g, ''))`,
// which coerced instead of rejecting and broke the module's own no-op
// contract for every numeric cell:
//   ''  → 0      '   ' → 0     'abc' → 0     'N/A' → 0
//   '7,50' → 750     '1.5M' → 1.5    '250k' → 250    '1e6' → 16
// Clearing a budget cell wrote 0, and a European-decimal CPM of 7,50 wrote a
// $750 CPM. Both are silent, both ride into a launch.
//
// The grammar accepted here is exactly what a reviewer can type unambiguously:
//   - surrounding whitespace, and one currency symbol (either side of a sign);
//   - an optional leading '-';
//   - digits, with ',' allowed ONLY as a thousands separator — the strict
//     \d{1,3}(,\d{3})+ shape, so '1,000' is 1000 but '7,50' is ambiguous and
//     therefore null;
//   - at most one '.' decimal part.
// Everything else is null: no digits at all, unit suffixes ('1.5M', '250k',
// '70%', '$6.50 CPA'), scientific notation ('1e6'), and any leftover
// character. Callers MUST treat null as "leave the plan alone" — the cell then
// re-renders the value it already had, which is how the user learns the draft
// wasn't understood.
//
// Negatives parse because the grammar names them explicitly rather than
// letting a stray '-' through; no current caller has a meaningful use for one
// (every target is a budget, an impression volume, a CPM or a bid), so a
// negative is simply passed on to the same range checks the writers already do.
const NUMERIC_DRAFT_RE = /^[$£€¥₹]?-?[$£€¥₹]?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/;

export const numeric = (v) => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  // \s covers the non-breaking space a pasted spreadsheet cell brings along.
  const cleanedDraft = String(v ?? '').replace(/\s+/g, '');
  if (!NUMERIC_DRAFT_RE.test(cleanedDraft)) return null;
  const n = Number(cleanedDraft.replace(/[,$£€¥₹]/g, ''));
  return Number.isFinite(n) ? n : null;
};

// ── Parent-scaled arithmetic ─────────────────────────────────────────
// Three grid cells (budget / imps / dailyCap) display a PER-ROW SHARE of a
// value stored on the parent entity, so committing one multiplies back up by
// the row's sibling count. These two helpers are the single source of that
// arithmetic: the appliers below/next door write through them, and
// CellDetailPanel's pre-commit preview reads through them. A preview that
// disagrees with what is written is worse than no preview, so there is
// deliberately only one copy of the maths.

/** share × siblings — the value the parent entity ends up carrying. */
export const scaleToParent = (share, siblingCount) => {
  if (share == null || !Number.isFinite(share)) return null;
  const n = Number(siblingCount);
  return share * (Number.isFinite(n) && n > 0 ? n : 1);
};

/**
 * DV360's budget cell is DERIVED (cpm × imps / 1000), so a typed budget is
 * persisted by solving back for impressions at the row's effective CPM.
 * No positive CPM → nothing to solve against → null.
 */
export const dv360ImpsFromBudget = (budget, cpm) => {
  if (budget == null || !Number.isFinite(budget)) return null;
  if (cpm == null || !Number.isFinite(cpm) || cpm <= 0) return null;
  return Math.round((budget / cpm) * 1000);
};

// ── List-op normalization ────────────────────────────────────────────
// The cell detail panel stages list operations as
// { op: 'remove' | 'add' | 'edit', value, newValue? }; legacy callers (and the
// copy grid) pass bare label strings, which mean "remove". Every applier
// funnels through ops internally, so both shapes are accepted everywhere.

export const toListOps = (arr) => (Array.isArray(arr) ? arr : [])
  .map((entry) => (entry && typeof entry === 'object' && entry.op
    ? entry
    : { op: 'remove', value: entry }))
  .filter((o) => o.value != null && o.value !== '');

/** One row's { field: ops|labels } map → { field: ops }, empty fields pruned. */
export const normalizeRowOps = (fields) => {
  const out = {};
  for (const [field, arr] of Object.entries(fields || {})) {
    const ops = toListOps(arr);
    if (ops.length) out[field] = ops;
  }
  return out;
};

// ── Lookup-picked list values ────────────────────────────────────────
// Some list adds need a platform ID, not just a label: a TTD geo segment is
// only launchable with its GeoSegmentId, a resolved TTD audience with its
// ThirdPartyDataId. The detail panel's async picker therefore stages the
// chosen row as a single ENCODED STRING — `<id><SEP><label>` — rather than an
// object, because the whole list-op model (staging toggle, dedupe, edit
// matching) compares op values with `===`. Keeping the value a primitive means
// none of that has to change.
//
// The separator is ASCII UNIT SEPARATOR, not a printable character: TTD
// ThirdPartyDataIds are themselves pipe-joined ("22252957|factualgeo"), and
// audience FullPaths / deal names carry their own punctuation, so any visible
// delimiter would eventually split an id or a label down the middle. U+001F
// can't appear in either, survives JSON, and compares with ===.
//
// The label half is whatever the row builder renders for that entity, so an
// add round-trips through the builder and can be removed again by label.
export const LOOKUP_VALUE_SEP = '\u001F';

/** Encode a picked lookup row into the `<id><SEP><label>` list-op value. */
export const encodeLookupValue = (id, label) => (
  `${String(id ?? '').trim()}${LOOKUP_VALUE_SEP}${String(label ?? '').trim()}`
);

/**
 * Encode a pick whose identity needs MORE than an id — GroundTruth taxonomy
 * records are keyed by (id, type) because 171 ids collide across types.
 * Wire form: `<id><SEP><type><SEP><label>`; decodeLookupValue reads it back.
 */
export const encodeTypedLookupValue = (id, type, label) => (
  `${String(id ?? '').trim()}${LOOKUP_VALUE_SEP}${String(type ?? '').trim()}${LOOKUP_VALUE_SEP}${String(label ?? '').trim()}`
);

/**
 * Decode a list-op value staged by the lookup picker. A plain typed value (no
 * separator) decodes to `{ id: null, label: <value> }` — the appliers then know
 * they have a label with no resolvable platform id. A typed pick (three parts)
 * also returns its `type`.
 */
export function decodeLookupValue(value) {
  const s = value == null ? '' : String(value);
  const parts = s.split(LOOKUP_VALUE_SEP);
  if (parts.length < 2) return { id: null, label: s };
  if (parts.length >= 3) {
    return {
      id: parts[0].trim() || null,
      type: parts[1].trim() || null,
      label: parts[2].trim(),
    };
  }
  return { id: parts[0].trim() || null, label: parts[1] };
}

/** Display form of a staged lookup value — the label half only. */
export const lookupValueLabel = (value) => decodeLookupValue(value).label;

// 'YYYY-MM-DD' (the native date editor's output) → DV360's {year, month, day}.
export function ymdToDv360Obj(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  if (!m) return null;
  return { year: Number(m[1]), month: Number(m[2]), day: Number(m[3]) };
}

// 'YYYY-MM-DD' → the ISO form the TTD launchers emit ('%Y-%m-%dT00:00:00'),
// so an edited date is indistinguishable from a built one to the TTD
// validator's datetime.fromisoformat.
export function ymdToTtdIso(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ''));
  return m ? `${m[1]}-${m[2]}-${m[3]}T00:00:00` : null;
}

// ── TTD ──────────────────────────────────────────────────────────────

// Money can be a bare float (what the copy launcher emits) or {Amount,
// CurrencyCode} (the MMGY parsed_response shape); preserve whichever shape
// is already there.
export function writeTtdMoney(obj, key, value) {
  const n = numeric(value);
  if (n == null) return;
  if (obj[key] && typeof obj[key] === 'object') obj[key].Amount = n;
  else obj[key] = n;
}

// Inverse of ttdConstants.pacingLabel, restricted to real TTD PacingMode
// values (ttd_semantic_validator's set plus PaceEvenly, which imported
// campaigns carry through). Unrecognized input → null → no-op.
export function ttdPacingModeFromDraft(draft) {
  const s = String(draft).trim().toLowerCase();
  if (!s) return null;
  if (s.includes('ahead')) return 'PaceAhead';
  if (s.includes('even')) return 'PaceEvenly';
  if (s.includes('end') || s.includes('flight')) return 'PaceToEndOfFlight';
  if (s === 'off') return 'Off';
  return null;
}

// ROIGoal amount — the goal's KIND (CPA/CPM/CTR/…) stays what the plan
// carries; only the target number is edited. Percent-valued goals
// (…InPercent) are bare numbers, currency goals wrap {Amount}.
export function writeTtdKpiTarget(ag, value) {
  if (!ag.ROIGoal || typeof ag.ROIGoal !== 'object') return;
  const goalKey = Object.keys(ag.ROIGoal)[0];
  if (!goalKey) return;
  const n = numeric(value);
  if (n == null) return;
  if (ag.ROIGoal[goalKey] && typeof ag.ROIGoal[goalKey] === 'object') {
    ag.ROIGoal[goalKey].Amount = n;
  } else {
    ag.ROIGoal[goalKey] = n;
  }
}

// IsEnabled, from the ENABLED/PAUSED label the row builders render.
// Unrecognized input → no-op.
//
// NOT REACHABLE FROM ANY GRID: `entityStatus` is denylisted on every review
// surface and no applier routes to this function any more (activation is the
// launch action's job — see the denylist comments in clwGridConstants.js).
// It stays here, fixed and tested, because it used to be reachable and was
// INVERTED: the old test was /enab|activ/, whose 'activ' matched the substring
// inside "Inactive", so the two most natural ways to say "off" both turned an
// ad group ON:
//   writeTtdEntityStatus(ag, 'Inactive')    → IsEnabled = true
//   writeTtdEntityStatus(ag, 'not enabled') → IsEnabled = true
// Matching is now on whole tokens with the negations checked FIRST, so only an
// affirmative token on its own can produce `true`.
const TTD_STATUS_TRUE = new Set(['enabled', 'enable', 'active', 'on', 'true', 'running', 'live']);
const TTD_STATUS_FALSE = new Set([
  'paused', 'pause', 'disabled', 'disable', 'inactive', 'off', 'false', 'stopped', 'archived',
]);
export function writeTtdEntityStatus(ag, value) {
  const tokens = String(value ?? '').trim().toLowerCase().split(/[^a-z]+/).filter(Boolean);
  if (!tokens.length) return;
  // A negation anywhere ("not enabled", "no longer active") means false, and is
  // checked before the affirmatives so it can never be out-voted.
  if (tokens.some((t) => TTD_STATUS_FALSE.has(t) || t === 'not' || t === 'no')) {
    ag.IsEnabled = false;
    return;
  }
  // Only a lone affirmative token enables — a phrase we don't fully recognize
  // is a no-op, not an activation.
  if (tokens.length === 1 && TTD_STATUS_TRUE.has(tokens[0])) ag.IsEnabled = true;
}

// Inverse of ttdConstants.formatFrequencyCap ("N/period"). Edits the config
// entry the formatter surfaces (the longest reset interval); shorter pacing
// throttles are preserved untouched. 'No cap' clears the list; unparseable
// input is a no-op.
const TTD_FREQ_PERIOD_MINUTES = { month: 40320, week: 10080, day: 1440, hour: 60 };
export function applyTtdFreqCap(ag, draft) {
  const s = String(draft).trim().toLowerCase();
  if (!s) return;
  if (s === 'no cap' || s === 'none' || s === '—') { ag.NewFrequencyConfigs = []; return; }
  const m = /^(\d+)\s*\/\s*(.+)$/.exec(s);
  if (!m) return;
  const cap = Number(m[1]);
  const periodStr = m[2].trim();
  let mins = TTD_FREQ_PERIOD_MINUTES[periodStr.replace(/s$/, '')];
  if (mins == null) {
    const mm = /^(\d+)\s*m$/.exec(periodStr);
    if (mm) mins = Number(mm[1]);
  }
  if (!Number.isFinite(mins) || mins <= 0 || !Number.isFinite(cap)) return;
  if (!Array.isArray(ag.NewFrequencyConfigs)) ag.NewFrequencyConfigs = [];
  const configs = ag.NewFrequencyConfigs;
  if (!configs.length) { configs.push({ FrequencyCap: cap, ResetIntervalInMinutes: mins }); return; }
  let top = configs[0];
  for (const c of configs) {
    if (Number(c.ResetIntervalInMinutes || 0) > Number(top.ResetIntervalInMinutes || 0)) top = c;
  }
  top.FrequencyCap = cap;
  top.ResetIntervalInMinutes = mins;
}

// ROIGoal wrapper-key rename through the kpi cell's own vocabulary (the
// builder strips the InAdvertiserCurrency/InPercent suffix for display).
// The value shape follows the key: currency goals wrap {Amount,
// CurrencyCode}, percent/ratio goals are bare numbers. Unrecognized token
// or absent ROIGoal → no-op (a type with no target is meaningless).
const TTD_KPI_KEYS = {
  cpa: 'CPAInAdvertiserCurrency',
  cpc: 'CPCInAdvertiserCurrency',
  cpm: 'CPMInAdvertiserCurrency',
  vcr: 'VCRInPercent',
  ctr: 'CTRInPercent',
  viewability: 'ViewabilityInPercent',
  roas: 'ReturnOnAdSpendPercent',
  returnonadspendpercent: 'ReturnOnAdSpendPercent',
};
export function applyTtdKpiType(ag, draft) {
  const newKey = TTD_KPI_KEYS[String(draft).trim().toLowerCase()];
  if (!newKey || !ag.ROIGoal || typeof ag.ROIGoal !== 'object') return;
  const oldKey = Object.keys(ag.ROIGoal)[0];
  if (!oldKey || oldKey === newKey) return;
  const oldVal = ag.ROIGoal[oldKey];
  const rawAmount = oldVal && typeof oldVal === 'object' ? Number(oldVal.Amount) : Number(oldVal);
  const amount = Number.isFinite(rawAmount) ? rawAmount : 0;
  ag.ROIGoal = /InAdvertiserCurrency$/.test(newKey)
    ? { [newKey]: { Amount: amount, CurrencyCode: (oldVal && typeof oldVal === 'object' && oldVal.CurrencyCode) || 'USD' } }
    : { [newKey]: amount };
}

// ── DV360 ────────────────────────────────────────────────────────────

const DV360_PACING_TYPES = new Set(['AHEAD', 'EVEN', 'ASAP']);
const DV360_PACING_PERIODS = new Set(['DAILY', 'FLIGHT']);
const DV360_ENTITY_STATUSES = new Set(['ACTIVE', 'PAUSED', 'DRAFT', 'ARCHIVED']);
const DV360_TIME_UNITS = new Set(['MINUTES', 'HOURS', 'DAYS', 'WEEKS', 'MONTHS', 'LIFETIME']);

// "AHEAD · FLIGHT" (the cell's own display format; '/' tolerated too) →
// li.pacing.pacingType/pacingPeriod.
export function applyDv360Pacing(li, draft) {
  const parts = String(draft).split(/[·/]/).map((s) => s.trim().toUpperCase()).filter(Boolean);
  const pacing = li.pacing || (li.pacing = {});
  for (const part of parts) {
    if (DV360_PACING_TYPES.has(part)) pacing.pacingType = `PACING_TYPE_${part}`;
    else if (DV360_PACING_PERIODS.has(part)) pacing.pacingPeriod = `PACING_PERIOD_${part}`;
  }
}

// "5/1 week" (the cell's own display format) → li.frequencyCap.
//
// "unlimited" is deliberately NOT writable here. A DV360 line item may not
// launch uncapped: the copy launcher substitutes the campaign cap, then
// 10 impressions/day, rather than emit one (_line_item_freq_cap), and the
// validator rejects it. Unlimited capping belongs on the insertion order or
// campaign, which own it in DV360's hierarchy. Typing it is a no-op, the same
// as any other input this writer can't express.
export function applyDv360FreqCap(li, draft) {
  const s = String(draft).trim();
  const m = /^(\d+)\s*\/\s*(\d+)\s+([a-z]+?)s?$/i.exec(s);
  if (!m) return; // unparseable → no-op, the cell re-renders unchanged
  const unit = `${m[3].toUpperCase()}S` === 'LIFETIMES' ? 'LIFETIME' : `${m[3].toUpperCase()}S`;
  if (!DV360_TIME_UNITS.has(unit)) return;
  li.frequencyCap = {
    unlimited: false,
    maxImpressions: Number(m[1]),
    timeUnitCount: Number(m[2]),
    timeUnit: `TIME_UNIT_${unit}`,
  };
}

// Daily pacing cap. The read path (buildDv360ReviewRows) prefers
// dailyMaxMicros over dailyMaxImpressions, so write whichever key the plan
// carries — the grid then round-trips the exact number the user typed with
// no unit conversion (the launcher stores impression caps under
// dailyMaxMicros on some plans, per the row builder's own comment).
export function writeDv360DailyCap(li, value) {
  const n = numeric(value);
  if (n == null) return;
  const pacing = li.pacing || (li.pacing = {});
  if (Object.prototype.hasOwnProperty.call(pacing, 'dailyMaxMicros')) pacing.dailyMaxMicros = n;
  else pacing.dailyMaxImpressions = n;
}

// li.entityStatus through the enum tail the row renders (ACTIVE / PAUSED /
// DRAFT / ARCHIVED). Unrecognized token → no-op.
//
// NOT REACHABLE FROM ANY GRID, for the same reason as writeTtdEntityStatus
// above: activation is the launch action's job, `entityStatus` is denylisted on
// every surface, and neither applier routes here. Unlike the TTD writer this
// one was never inverted — it matches against an exact enum set, so 'Inactive'
// was already a no-op — but it is kept beside its sibling so the two platforms'
// status handling stays in one place if the field is ever reopened under an
// explicit activation affordance.
export function writeDv360EntityStatus(li, value) {
  const token = String(value).trim().toUpperCase().replace(/^ENTITY_STATUS_/, '');
  if (DV360_ENTITY_STATUSES.has(token)) li.entityStatus = `ENTITY_STATUS_${token}`;
}

// li.bidStrategy.<key>.{maxAverageCpmBidAmountMicros|bidAmountMicros} in
// dollars — mirrors the read path (maxAverageCpmBidAmountMicros preferred,
// bidAmountMicros only when it's the detail's existing sole bid key) so
// saving N matches what the row shows.
export function writeDv360BidMicros(li, value) {
  const dollars = numeric(value);
  if (dollars == null) return;
  const bidObj = li.bidStrategy || {};
  const bidKey = Object.keys(bidObj)[0];
  if (!bidKey) return;
  const detail = bidObj[bidKey] || (bidObj[bidKey] = {});
  const micros = Math.round(dollars * 1e6);
  if (Object.prototype.hasOwnProperty.call(detail, 'maxAverageCpmBidAmountMicros')
      || !Object.prototype.hasOwnProperty.call(detail, 'bidAmountMicros')) {
    detail.maxAverageCpmBidAmountMicros = micros;
  } else {
    detail.bidAmountMicros = micros;
  }
}

// bidStrategy → swap the li.bidStrategy wrapper key, preserving the inner
// detail object (micros / performance goal survive the strategy change).
// Tokens are the cell's own labels ('Fixed Bid', 'Maximize Spend Auto Bid',
// 'Performance Goal Auto Bid') or the raw camelCase keys.
const DV360_BID_KEYS = {
  fixedbid: 'fixedBid',
  maximizespendautobid: 'maximizeSpendAutoBid',
  performancegoalautobid: 'performanceGoalAutoBid',
};
export function applyDv360BidStrategy(li, draft) {
  const newKey = DV360_BID_KEYS[String(draft).replace(/[^a-zA-Z]/g, '').toLowerCase()];
  if (!newKey) return;
  const bidObj = li.bidStrategy || {};
  const oldKey = Object.keys(bidObj)[0];
  if (oldKey === newKey) return;
  li.bidStrategy = { [newKey]: (oldKey ? bidObj[oldKey] : null) || {} };
}

// bidGoal → the bid detail's performanceGoalType, re-prefixed. Unrecognized
// token or no bid-strategy key to hang it on → no-op.
const DV360_BID_GOALS = new Set(['CPA', 'CPC', 'VIEWABLE_CPM', 'CUSTOM_ALGO', 'CIVA', 'IVO_TEN', 'AV_VIEWED']);
export function applyDv360BidGoal(li, draft) {
  const token = String(draft).trim().toUpperCase()
    .replace(/^BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_/, '').replace(/[\s-]+/g, '_');
  if (!DV360_BID_GOALS.has(token)) return;
  const bidObj = li.bidStrategy || {};
  const bidKey = Object.keys(bidObj)[0];
  if (!bidKey) return;
  const detail = bidObj[bidKey] || (bidObj[bidKey] = {});
  detail.performanceGoalType = `BIDDING_STRATEGY_PERFORMANCE_GOAL_TYPE_${token}`;
}

// expansion → targetingExpansion.enableOptimizedTargeting, through the
// cell's 'Optimized targeting' / '—' rendering (booleans tolerated).
export function applyDv360Expansion(li, draft) {
  const s = String(draft).trim().toLowerCase();
  let flag = null;
  if (s.includes('optimi') || /^(true|yes|on|enabled?)$/.test(s)) flag = true;
  else if (['false', 'no', 'off', 'none', '—', '-'].includes(s)) flag = false;
  if (flag == null) return;
  const te = li.targetingExpansion || (li.targetingExpansion = {});
  te.enableOptimizedTargeting = flag;
}

// "30d click · 7d view" (the convWindow cell's own rendering) → the two
// lookback-day numbers, or null when neither part parses.
export function parseDv360ConvWindow(draft) {
  if (draft == null) return null;
  const click = /(\d+)\s*d?\s*click/i.exec(String(draft));
  const view = /(\d+)\s*d?\s*view/i.exec(String(draft));
  if (!click && !view) return null;
  return { click: click ? Number(click[1]) : 30, view: view ? Number(view[1]) : 30 };
}

// pixel → conversionCounting.floodlightActivityConfigs[0].floodlightActivityId,
// parsing the cell's 'Floodlight #N' rendering (bare ids tolerated); 'None'
// clears the configs. When creating a config from scratch, the launcher
// requires id + both lookback windows non-empty — seed the windows from a
// convWindow edit in the same batch, else 30/30.
export function applyDv360Pixel(li, draft, convWindowDraft) {
  const s = String(draft).trim();
  if (!s || /^(none|—|-)$/i.test(s)) {
    if (li.conversionCounting) li.conversionCounting.floodlightActivityConfigs = [];
    return;
  }
  const m = /#\s*([\w-]+)\s*$/.exec(s) || /^([\w-]+)$/.exec(s.replace(/^floodlight\s*/i, '').trim());
  if (!m) return;
  const cc = li.conversionCounting || (li.conversionCounting = {});
  const configs = Array.isArray(cc.floodlightActivityConfigs)
    ? cc.floodlightActivityConfigs
    : (cc.floodlightActivityConfigs = []);
  if (configs[0]) {
    configs[0].floodlightActivityId = m[1];
    return;
  }
  const win = parseDv360ConvWindow(convWindowDraft);
  configs.push({
    floodlightActivityId: m[1],
    postClickLookbackWindowDays: win ? win.click : 30,
    postViewLookbackWindowDays: win ? win.view : 30,
  });
}

// convWindow → the first floodlight config's lookback windows. No config to
// hang windows on (and no pixel edit creating one first) → no-op.
export function applyDv360ConvWindow(li, draft) {
  const win = parseDv360ConvWindow(draft);
  if (!win) return;
  const config = li.conversionCounting?.floodlightActivityConfigs?.[0];
  if (!config) return;
  config.postClickLookbackWindowDays = win.click;
  config.postViewLookbackWindowDays = win.view;
}

// kpi → the parent IO's kpi.kpiType with the KPI_TYPE_ prefix restored.
// Unrecognized token → no-op.
const DV360_KPI_TYPES = new Set([
  'CPM', 'CPC', 'CPA', 'CTR', 'CPV', 'CPE', 'CPIAVC', 'VCPM', 'VTR',
  'CLICK_CVR', 'IMPRESSION_CVR', 'AUDIO_COMPLETION_RATE', 'VIDEO_COMPLETION_RATE',
  'VIEWABILITY_PERCENT', 'CUSTOM_BIDDING_VALUE', 'PERCENT_VIDEO_VIEWS',
  'QUALITY_IMPRESSIONS', 'OTHER',
]);
export function applyDv360IoKpiType(io, draft) {
  const token = String(draft).trim().toUpperCase()
    .replace(/^KPI_TYPE_/, '').replace(/[\s-]+/g, '_');
  if (!DV360_KPI_TYPES.has(token)) return;
  const kpi = io.kpi || (io.kpi = {});
  kpi.kpiType = `KPI_TYPE_${token}`;
}

// Flight dates live on the IO's budget segments — the row builder reads
// start from segments[0].dateRange and end from segments[last].dateRange, so
// this round-trips in the grid. The campaign-level flight
// (campaignFlight.plannedDates) is kept in step so the campaign can't state
// a flight its IO has been edited out of; with several IOs the last edit
// wins there, which matches the single-flight semantics both hosts want.
export function applyDv360FlightDates(edited, io, fields) {
  if (!io || (fields.start == null && fields.end == null)) return;
  const segments = Array.isArray(io.budget?.budgetSegments) ? io.budget.budgetSegments : [];
  const planned = edited.campaign?.campaignFlight?.plannedDates;
  if (!segments.length) return;
  if (fields.start != null) {
    const obj = ymdToDv360Obj(fields.start);
    if (obj) {
      const range = segments[0].dateRange || (segments[0].dateRange = {});
      range.startDate = obj;
      if (planned && planned.startDate) planned.startDate = { ...obj };
    }
  }
  if (fields.end != null) {
    const obj = ymdToDv360Obj(fields.end);
    if (obj) {
      const last = segments[segments.length - 1];
      const range = last.dateRange || (last.dateRange = {});
      range.endDate = obj;
      if (planned && planned.endDate) planned.endDate = { ...obj };
    }
  }
}
